import React, { useState, useMemo, useRef } from "react";
import { C, font, inp, lbl, btnP, btnS } from "../../../lib/styles.js";
import { MAX_ATTACHMENT_BYTES, isAttachmentTooLarge, compressImage } from "../../../lib/builderHelpers.js";
import { aiMediaType, planAiSend, hasReadableSource } from "../../../lib/attachmentBytes.js";
import { splitBrief, joinBrief, nextNotesSource, notesAreOperator } from "../../../lib/ai.js";

// "One brief" (a single box, split at the YOUR NOTES line) or the classic two boxes.
// Remembered per browser; the quote fields underneath are the same either way.
const MODE_KEY = "qb.aiInputMode";
const readMode = () => { try { return localStorage.getItem(MODE_KEY) === "one" ? "one" : "two"; } catch { return "two"; } };
const writeMode = (m) => { try { localStorage.setItem(MODE_KEY, m); } catch { /* private mode etc. */ } };

/*
  QuoteAIInput — the AI "paste & extract" front door to the Quote Builder.
  Live (Slice 1): "Extract & Build" sends the pasted email + notes to Claude via
  the admin-gated claudeProxy callable, applies the structured result to the
  quote, and drops into the manual builder for review. Text-only for now —
  AI reading of attached files lands in a later slice.

  Props:
    quote          current quote object
    set(f, v)      field setter (same as QuoteBuilder's `set`)
    onExtract({emailText, notesText, notesTrusted})  async; runs extraction + applies result
                   (advances step -> "build"). Throws on failure.
    onContinue()   advance to the manual builder without AI (sets step -> "build")
    onSaveDraft()/saving  draft save
    onOpenSaved()  back to the saved-quotes list
*/
export default function QuoteAIInput({ quote, set, onExtract, onContinue, onSaveDraft, saving, onOpenSaved }) {
  const [attachments, setAttachments] = useState(quote.attachments || []);
  // Mirror attachments into a ref so the async addFiles can merge against the
  // latest list — two quick adds (each awaiting compressImage) would otherwise
  // both read a stale closure value and the later one would drop the earlier.
  const attRef = useRef(attachments);
  attRef.current = attachments;
  const [status, setStatus] = useState("idle"); // idle | extracting | error
  const [errorMsg, setErrorMsg] = useState("");
  const [rejected, setRejected] = useState([]); // names of oversized files
  const emailText = quote.emailText || "";
  const notesText = quote.notesText || "";
  // One-box mode: the raw paste lives here; every keystroke is split into the two
  // quote fields so extraction, the CRM sender match and saved quotes are unchanged.
  const [mode, setMode] = useState(readMode);
  const [brief, setBrief] = useState(() => joinBrief({ emailText: quote.emailText, notesText: quote.notesText }));
  const briefSplit = mode === "one" ? splitBrief(brief).split : true;
  const switchMode = (m) => {
    if (m === "one") setBrief(joinBrief({ emailText, notesText }));
    setMode(m);
    writeMode(m);
  };
  const onBriefChange = (raw) => {
    setBrief(raw);
    const parts = splitBrief(raw);
    set("emailText", parts.emailText);
    set("notesText", parts.notesText);
    // Who wrote the notes, kept on the quote so a reopen or a mode switch (both
    // rebuild the paste with a YOUR NOTES line) cannot promote them (2026-10-09):
    // notes cut at a bare "Notes" line may be the client's own text.
    set("notesSource", nextNotesSource(quote.notesSource, notesText, raw));
  };
  // Extract is allowed with text, notes, OR at least one readable PDF/image —
  // the AI can build from an attached brief alone (F3).
  // A readable file must be a PDF/image AND actually carry bytes (F1 r4).
  const hasReadableFile = attachments.some(a => (a.type === "image" || a.type === "pdf") && hasReadableSource(a));
  const canExtract = emailText.trim().length > 0 || notesText.trim().length > 0 || hasReadableFile;
  const maxMb = Math.round(MAX_ATTACHMENT_BYTES / (1024 * 1024));
  // Per-row AI-send plan (source + type + size + aggregate budget), keyed by id,
  // in list order — the same planner extractFromAI uses, so labels can't
  // overpromise. Metadata-only rows (no bytes) are excluded → "saved, not read".
  const aiPlanById = useMemo(() => {
    const items = attachments
      .filter(a => (a.type === "image" || a.type === "pdf") && hasReadableSource(a))
      .map(a => ({ id: a.id, name: a.name, kind: a.type, mediaType: aiMediaType(a.file?.type, a.name, a.type), bytes: a.bytes || 0 }));
    return Object.fromEntries(planAiSend(items).map(p => [p.id, p]));
  }, [attachments]);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const tooBig = files.filter(f => isAttachmentTooLarge(f.size));
    if (tooBig.length) setRejected(tooBig.map(f => f.name));
    const next = await Promise.all(files.filter(f => !isAttachmentTooLarge(f.size)).map(async f => {
      const isImage = f.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp|heic|tiff?)$/i.test(f.name);
      // Make a local preview copy for images so the builder thumbnail + PDF
      // addendum can render them — without this the builder shows a grey "No
      // preview" box (the builder's own addAttachments does the same). The AI
      // send still reads the raw `file`; compressImage can fail → no dataUrl.
      let dataUrl = null;
      if (isImage) { try { dataUrl = await compressImage(f); } catch { dataUrl = null; } }
      return {
        id: "att_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6),
        name: f.name,
        type: isImage ? "image" : f.name.toLowerCase().endsWith(".pdf") ? "pdf" : "doc",
        // Raw byte count for the size cap; `file` is the in-memory File the AI
        // extraction reads (transient — saveDraft strips it to lean fields).
        bytes: f.size,
        file: f,
        size: f.size < 1024 ? f.size + " B" : f.size < 1048576 ? (f.size / 1024).toFixed(1) + " KB" : (f.size / 1048576).toFixed(1) + " MB",
        ...(dataUrl ? { dataUrl } : {}),
      };
    }));
    const merged = [...attRef.current, ...next];
    attRef.current = merged;
    setAttachments(merged);
    set("attachments", merged);
  };

  const removeAtt = (id) => {
    const merged = attachments.filter(a => a.id !== id);
    setAttachments(merged);
    set("attachments", merged);
  };

  const runExtract = async () => {
    if (!canExtract || status === "extracting") return;
    setStatus("extracting");
    setErrorMsg("");
    try {
      // On success onExtract advances step -> "build", unmounting this screen.
      // A quote discount line counts only in notes the operator wrote: typed in the
      // Your Notes box, or under a YOUR NOTES style line in One brief (2026-10-09).
      const notesTrusted = notesAreOperator(quote);
      await onExtract({ emailText, notesText, notesTrusted });
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.message || "Extraction failed. Try again, or skip to the manual builder.");
    }
  };

  return (
    <div className="qb-scope" style={{ fontFamily: font, animation: "fadeUp .3s ease" }}>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
        @keyframes dp{0%,60%,100%{opacity:.25}30%{opacity:1}}
        .qb-scope textarea:focus, .qb-scope input:focus{border-color:${C.text} !important;box-shadow:0 0 0 3px rgba(26,26,26,0.05);}
        .qb-scope textarea:hover:not(:focus){border-color:#CFCBC4;}`}</style>

      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.kicker}>Quote Builder · AI Start</div>
          <div style={S.title}>New quote</div>
        </div>
        <div style={S.headerActions}>
          <button type="button" style={btnS} onClick={onOpenSaved}>Saved Quotes</button>
          <button type="button" style={{ ...btnS, color: C.amber, borderColor: `${C.amber}45` }} disabled={saving} onClick={onSaveDraft}>Save Draft</button>
        </div>
      </div>

      <div style={S.shell}>
        {/* Input mode: one brief (Claude-written, split at YOUR NOTES) or two boxes */}
        <div style={S.modeRow} role="group" aria-label="Input mode">
          {[["one", "One brief"], ["two", "Two boxes"]].map(([m, text]) => (
            <button key={m} type="button" onClick={() => switchMode(m)} aria-pressed={mode === m}
              style={{ ...S.modePill, ...(mode === m ? S.modePillOn : {}) }}>{text}</button>
          ))}
          <span style={S.modeHint}>{mode === "one"
            ? "Paste the whole brief. A line reading YOUR NOTES separates the client email above it from your instructions below it."
            : "The client's email in the first box, your instructions in the second."}</span>
        </div>

        {mode === "one" ? (
          <section style={S.block}>
            <div style={S.blockHead}>
              <span style={S.blockLabel}>Brief</span>
              <span style={{ ...S.hintItalic, color: briefSplit ? C.amber : C.muted }}>
                {briefSplit ? "notes found, they outrank the email" : brief.trim() ? "no YOUR NOTES line yet, everything counts as client communication" : "client communication, then YOUR NOTES, then your instructions"}
              </span>
            </div>
            <textarea
              value={brief}
              onChange={e => onBriefChange(e.target.value)}
              placeholder={"From: Client <client@example.com>\nSubject: ...\n\n(the email, call notes)\n\nYOUR NOTES\n(your instructions: rates, what to price, what not to)"}
              rows={16}
              aria-label="Brief"
              style={{ ...inp, background: C.surface, resize: "vertical", lineHeight: 1.7, fontSize: 13, fontWeight: 400 }}
            />
          </section>
        ) : (
          <>
            {/* Client Communication */}
            <section style={S.block}>
              <div style={S.blockHead}>
                <span style={S.blockLabel}>Client Communication</span>
                <span style={S.hintItalic}>what the client sent you</span>
              </div>
              <div style={S.hint}>Paste emails, forward chains, or call notes. AI extracts project details, contacts, dates, and line items.</div>
              <textarea
                value={emailText}
                onChange={e => set("emailText", e.target.value)}
                placeholder="Paste an email, describe a phone call, or drop in any project details…"
                rows={11}
                style={{ ...inp, background: C.surface, resize: "vertical", lineHeight: 1.7, fontSize: 13, fontWeight: 400 }}
              />
            </section>

            {/* Your Notes */}
            <section style={S.block}>
              <div style={S.blockHead}>
                <span style={{ ...S.blockLabel, color: C.amber }}>Your Notes</span>
                <span style={{ ...S.hintItalic, color: C.amber }}>overrides client info</span>
              </div>
              <div style={S.hint}>Your interpretation and instructions. These take priority over everything else.</div>
              <textarea
                value={notesText}
                onChange={e => { set("notesText", e.target.value); set("notesSource", "operator"); }}
                placeholder={'e.g. "charge film rates" · "2-day shoot" · "give repeat discount" · "separate FBX line item"'}
                rows={4}
                style={{ ...inp, resize: "vertical", lineHeight: 1.7, fontSize: 13, borderColor: `${C.amber}40`, background: `${C.amber}06` }}
              />
            </section>
          </>
        )}

        {/* Attachments */}
        <section style={S.block}>
          <div style={S.blockHead}>
            <span style={S.blockLabel}>Attachments</span>
            <span style={{ ...S.hintItalic, color: C.muted }}>the AI reads attached PDFs &amp; images</span>
          </div>
          <div style={S.dropzone}>
            {rejected.length > 0 && (
              <div style={S.rejectBox}>
                Too large to read (over {maxMb} MB), not attached: {rejected.join(", ")}
                <button type="button" onClick={() => setRejected([])} style={S.rejectX}>×</button>
              </div>
            )}
            {attachments.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 10 }}>
                {attachments.map(a => {
                  // Plan against the SAME planner the send path uses, in list order,
                  // so the label reflects per-file checks AND the aggregate budget —
                  // no "AI reads" promise on a file that'll be skipped (F2/F3).
                  const p = aiPlanById[a.id];
                  const willRead = !!p?.willRead;
                  const sub = willRead ? "" : p?.reason ? ` · ${p.reason}, not read` : " · saved, not read";
                  return (
                    <div key={a.id} style={S.attRow}>
                      <span style={{ fontSize: 15 }}>{a.type === "pdf" ? "📄" : a.type === "image" ? "🖼" : "📎"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.attName}>{a.name}</div>
                        <div style={S.attSize}>{a.size}{sub}</div>
                      </div>
                      {willRead && <span style={S.readPill}>AI reads</span>}
                      <button type="button" onClick={() => removeAtt(a.id)} style={S.attX}>×</button>
                    </div>
                  );
                })}
              </div>
            )}
            <label style={S.attachBtn}>
              + Attach Files
              <input type="file" accept="image/*,.pdf,.doc,.docx" multiple style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            </label>
            {attachments.length === 0 && <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>PDFs &amp; images are read by the AI · Word docs saved only</span>}
          </div>
        </section>

        {/* Extraction error */}
        {status === "error" && (
          <div style={S.errorBox}>
            <strong style={{ fontWeight: 500 }}>Couldn’t extract.</strong> {errorMsg}
          </div>
        )}

        {/* Actions */}
        <div style={S.actions}>
          <button
            type="button"
            style={{ ...btnP, opacity: !canExtract || status === "extracting" ? 0.4 : 1 }}
            disabled={!canExtract || status === "extracting"}
            onClick={runExtract}
          >
            {status === "extracting"
              ? <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>Extracting
                  <span style={{ display: "inline-flex", gap: 4 }}>{[0, 1, 2].map(i => <span key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", animation: `dp 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}</span>
                </span>
              : "Extract & Build →"}
          </button>
          <button type="button" style={btnS} onClick={onContinue}>Skip AI → Manual</button>
        </div>
      </div>
    </div>
  );
}

const S = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 16 },
  kicker: { fontSize: 9.5, color: C.muted, textTransform: "uppercase", marginBottom: 4 },
  title: { fontSize: 20, color: C.text, fontWeight: 400 },
  headerActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  shell: { maxWidth: 720, margin: "0 auto", display: "grid", gap: 20 },
  modeRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  modePill: { ...btnS, padding: "6px 12px", fontSize: 12 },
  modePillOn: { background: C.text, color: "#FFF", borderColor: C.text },
  modeHint: { fontSize: 11.5, color: C.muted, marginLeft: 4, flex: "1 1 240px" },
  block: {},
  blockHead: { display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 },
  blockLabel: { fontSize: 15, fontWeight: 400, color: C.text },
  hintItalic: { fontSize: 10, color: C.muted, fontStyle: "italic" },
  hint: { fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 10 },
  dropzone: { border: `1px dashed ${C.border}`, borderRadius: 6, padding: "12px 16px", background: C.surfaceAlt },
  attRow: { display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", background: C.surface, borderRadius: 4, border: `1px solid ${C.border}` },
  attName: { fontSize: 12, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  attSize: { fontSize: 10, color: C.muted },
  attX: { background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 16, padding: "0 4px" },
  attachBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", fontSize: 12, color: C.blue, cursor: "pointer", border: `1px solid ${C.blue}30`, borderRadius: 4, background: "transparent" },
  readPill: { fontSize: 9.5, color: C.blue, background: `${C.blue}12`, borderRadius: 4, padding: "2px 6px", letterSpacing: "0.04em", whiteSpace: "nowrap" },
  rejectBox: { display: "flex", alignItems: "flex-start", gap: 8, background: `${C.amber}10`, border: `1px solid ${C.amber}40`, color: C.text, borderRadius: 4, padding: "8px 10px", fontSize: 11.5, lineHeight: 1.4, marginBottom: 10 },
  rejectX: { background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 2px", marginLeft: "auto" },
  errorBox: { background: `${C.danger}0D`, border: `1px solid ${C.danger}40`, color: C.text, borderRadius: 6, padding: "10px 13px", fontSize: 12.5, lineHeight: 1.5 },
  actions: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 4 },
};
